class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    
    // Handle Apply button click for mobile facets
    const applyButton = this.querySelector('.mobile-facets__apply-button');
    if (applyButton) {
      applyButton.addEventListener('click', (event) => {
        event.preventDefault();
        // Close the drawer immediately
        const mobileFacetsWrapper = applyButton.closest('.mobile-facets__wrapper');
        if (mobileFacetsWrapper) {
          const summary = mobileFacetsWrapper.querySelector('summary');
          if (summary) {
            summary.click();
          }
        }
      });
    }

    // Handle close button click inside drawer
    const closeButtonInner = this.querySelector('.mobile-facets__close-button-inner');
    if (closeButtonInner) {
      closeButtonInner.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        // Close the drawer by clicking the summary
        const mobileFacetsWrapper = closeButtonInner.closest('.mobile-facets__wrapper');
        if (mobileFacetsWrapper) {
          const summary = mobileFacetsWrapper.querySelector('summary');
          if (summary) {
            summary.click();
          }
        }
      });
    }

    // Handle click outside mobile-facets__inner to close the overlay
    const mobileFacetsForm = this.querySelector('#FacetFiltersFormMobile');
    const mobileFacetsInner = this.querySelector('.mobile-facets__inner');
    if (mobileFacetsForm && mobileFacetsInner) {
      mobileFacetsForm.addEventListener('click', (event) => {
        // Only close if clicking on the backdrop (not on the inner drawer)
        if (!mobileFacetsInner.contains(event.target) && event.target !== mobileFacetsInner) {
          const mobileFacetsWrapper = mobileFacetsForm.closest('.mobile-facets__wrapper');
          if (mobileFacetsWrapper) {
            const summary = mobileFacetsWrapper.querySelector('summary');
            if (summary && mobileFacetsWrapper.querySelector('details[open]')) {
              summary.click();
            }
          }
        }
      });
    }

    // Initialize custom sort dropdown
    this.initCustomSortDropdown();
  }

  initCustomSortDropdown() {
    const dropdown = this.querySelector('.custom-sort-dropdown');
    if (!dropdown) return;

    const button = dropdown.querySelector('.custom-sort-dropdown__button');
    const menu = dropdown.querySelector('.custom-sort-dropdown__menu');
    const options = dropdown.querySelectorAll('.custom-sort-dropdown__option');
    const hiddenSelect = dropdown.querySelector('.custom-sort-dropdown__hidden-select');
    const selectedText = dropdown.querySelector('.custom-sort-dropdown__selected');
    
    // Find the form - could be directly in this element or in a parent
    let form = this.querySelector('form');
    if (!form) {
      // Try finding the form by traversing up or by ID
      form = dropdown.closest('form');
      if (!form) {
        // For mobile, the form might be FacetFiltersFormMobile
        form = document.getElementById('FacetFiltersFormMobile');
      }
    }

    if (!button || !menu || !hiddenSelect || !form) return;

    // Get the sort label from the dropdown's data attribute
    const sortLabel = dropdown.dataset.sortLabel || 'Sort by';
    
    // Sync selected state from hidden select
    const currentValue = hiddenSelect.value;
    options.forEach((opt) => {
      opt.classList.remove('is-selected');
      if (opt.dataset.value === currentValue) {
        opt.classList.add('is-selected');
        // const text = opt.querySelector('span').textContent.trim();
        // if (text && text !== '') {
        //   // selectedText.textContent = `${sortLabel} ${text}`;
        //   selectedText.textContent = `${sortLabel}`;

        // } else {
        //   selectedText.textContent = `${sortLabel}`;
        // }
      }
    });

    // Toggle dropdown
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', !isOpen);
    });

    // Handle option selection
    options.forEach((option) => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        const value = option.dataset.value;
        const text = option.querySelector('span').textContent.trim();

        // Update hidden select
        hiddenSelect.value = value;
        
        // Create an input event object that mimics a real input event
        // We need to ensure event.target points to the select so the form handler works correctly
        const inputEvent = {
          target: hiddenSelect,
          currentTarget: form,
          type: 'input',
          bubbles: true,
          cancelable: true,
          preventDefault: function() {},
          stopPropagation: function() {},
          srcElement: hiddenSelect
        };
        
        // Directly call the debounced submit handler
        // This bypasses the event listener and ensures the form submission happens
        this.debouncedOnSubmit(inputEvent);

        // Get the sort label from the dropdown's data attribute
        const sortLabel = dropdown.dataset.sortLabel || 'Sort by';

        // Update selected state
        options.forEach((opt) => opt.classList.remove('is-selected'));
        option.classList.add('is-selected');

        // Close dropdown
        button.setAttribute('aria-expanded', 'false');
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!dropdown.contains(e.target)) {
        button.setAttribute('aria-expanded', 'false');
      }
    });

    // Handle keyboard navigation
    button.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        button.click();
      } else if (e.key === 'Escape') {
        button.setAttribute('aria-expanded', 'false');
      } else if (e.key === 'ArrowDown' && button.getAttribute('aria-expanded') === 'true') {
        e.preventDefault();
        const firstOption = options[0];
        if (firstOption) {
          firstOption.setAttribute('tabindex', '0');
          firstOption.focus();
        }
      }
    });

    // Make options focusable and handle keyboard navigation
    options.forEach((option, index) => {
      option.setAttribute('tabindex', '-1');
      
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          option.click();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          const next = options[index + 1] || options[0];
          options.forEach((opt) => opt.setAttribute('tabindex', '-1'));
          next.setAttribute('tabindex', '0');
          next.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = options[index - 1] || options[options.length - 1];
          options.forEach((opt) => opt.setAttribute('tabindex', '-1'));
          prev.setAttribute('tabindex', '0');
          prev.focus();
        } else if (e.key === 'Escape') {
          button.setAttribute('aria-expanded', 'false');
          button.focus();
        }
      });
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    // Preserve scroll position - use ScrollSmoother if available, otherwise native scroll
    const scrollPosition = window.scrollY;
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        // Restore scroll position after content update
        // Use double RAF to ensure DOM is fully rendered
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (window.scrollSmoother) {
              // Use ScrollSmoother scrollTo if available
              // ScrollSmoother.scrollTo(value, smooth) - value can be number or string
              window.scrollSmoother.scrollTo(scrollPosition, false); // false = immediate, true = smooth
            } else {
              window.scrollTo({ top: scrollPosition, left: 0, behavior: 'auto' });
            }
          });
        });
        // Initialize scroll animations on the document, not HTML string
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    // Preserve scroll position - use ScrollSmoother if available, otherwise native scroll
    const scrollPosition = window.scrollY;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    // Restore scroll position after content update
    // Use double RAF to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (window.scrollSmoother) {
          // Use ScrollSmoother scrollTo if available
          window.scrollSmoother.scrollTo(scrollPosition, false); // false = immediate, true = smooth
        } else {
          window.scrollTo({ top: scrollPosition, left: 0, behavior: 'auto' });
        }
      });
    });
    // Initialize scroll animations on the document, not HTML string
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(document);
  }

  static renderProductGridContainer(html) {
    // Preserve scroll position before DOM update
    const scrollPosition = window.scrollY;
    const container = document.getElementById('ProductGridContainer');
    container.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    // Cancel scroll triggers to prevent animation issues
    container
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    
    // Restore scroll position after DOM update
    // Use double RAF to ensure DOM is fully rendered and prevent scroll jumping
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (window.scrollSmoother) {
          // Use ScrollSmoother scrollTo if available
          window.scrollSmoother.scrollTo(scrollPosition, false); // false = immediate, true = smooth
        } else {
          window.scrollTo({ top: scrollPosition, left: 0, behavior: 'auto' });
        }
        
        // Re-initialize GSAP title animations after re-render
        if (typeof window.GSAPAnimations !== 'undefined' && window.GSAPAnimations.animateHeroTitleSplit) {
          const titleElements = container.querySelectorAll('.gsap-hero-title-split');
          titleElements.forEach((titleEl) => {
            // Check if element is already in viewport - if so, set to opacity 1 immediately
            const rect = titleEl.getBoundingClientRect();
            const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
            
            if (isInViewport) {
              // Element is already visible, set to opacity 1 immediately
              if (typeof gsap !== 'undefined') {
                gsap.set(titleEl, { opacity: 1 });
                titleEl.classList.add('_gsap-ready');
              }
            } else {
              // Element not yet in view, re-initialize animation
              window.GSAPAnimations.animateHeroTitleSplit(titleEl);
            }
          });
        }
      });
    });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const productCountElement = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    
    // Only update if ProductCount exists in the fetched HTML
    if (productCountElement && container) {
      const count = productCountElement.innerHTML;
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    
    // Update desktop count if it exists
    if (containerDesktop) {
      const desktopCountElement = parsedHTML.getElementById('ProductCountDesktop');
      if (desktopCountElement) {
        containerDesktop.innerHTML = desktopCountElement.innerHTML;
      } else if (productCountElement) {
        // Fallback to ProductCount if ProductCountDesktop doesn't exist
        containerDesktop.innerHTML = productCountElement.innerHTML;
      }
      containerDesktop.classList.remove('loading');
    }
    
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__summary`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
    
    // Re-initialize custom sort dropdowns after re-render
    document.querySelectorAll('facet-filters-form').forEach((form) => {
      if (form.initCustomSortDropdown) {
        form.initCustomSortDropdown();
      }
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  mergeFormParams(forms) {
    // Start with current URL parameters to preserve existing filters/sort
    const currentParams = new URLSearchParams(window.location.search);
    const mergedParams = new URLSearchParams();
    
    // Track which form params we've seen to avoid duplicates
    const formParamsSeen = new Set();
    
    // First, get all new parameters from the forms
    forms.forEach((form) => {
      const formData = new FormData(form);
      for (const [key, value] of formData.entries()) {
        if (value) {
          const paramKey = `${key}=${value}`;
          if (!formParamsSeen.has(paramKey)) {
            formParamsSeen.add(paramKey);
            if (key === 'sort_by') {
              // For sort_by, set (will overwrite)
              mergedParams.set(key, value);
            } else {
              // For filter params, append (can have multiple values)
              mergedParams.append(key, value);
            }
          }
        }
      }
    });
    
    // Then, add current URL params that aren't already in the merged params
    // This preserves existing filters/sort that weren't changed
    for (const [key, value] of currentParams.entries()) {
      const paramKey = `${key}=${value}`;
      
      // Skip if we already have this exact param from the form
      if (formParamsSeen.has(paramKey)) {
        continue;
      }
      
      if (key === 'sort_by') {
        // Only add current sort_by if form didn't provide a new one
        if (!mergedParams.has(key)) {
          mergedParams.set(key, value);
        }
      } else {
        // Add current filter params that weren't in the form
        mergedParams.append(key, value);
      }
    }
    
    return mergedParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    
    // Check if this was triggered by the Apply button
    const isApplyButton = event.target.closest('.mobile-facets__apply-button') !== null;
    
    if (event.srcElement.className == 'mobile-facets__checkbox' || isApplyButton) {
      // Get the form that triggered this (mobile filter form)
      const form = event.target.closest('form');
      const formsToMerge = form ? [form] : [];
      
      // Also get sort form to include any sort changes
      const sortDrawerForm = document.getElementById('FacetSortDrawerForm');
      if (sortDrawerForm && !formsToMerge.includes(sortDrawerForm)) {
        formsToMerge.push(sortDrawerForm);
      }
      
      const searchParams = this.mergeFormParams(formsToMerge);
      this.onSubmitForm(searchParams, event);
      
      // If triggered by Apply button, close the drawer after applying filters
      if (isApplyButton) {
        const mobileFacetsWrapper = event.target.closest('.mobile-facets__wrapper');
        if (mobileFacetsWrapper) {
          const summary = mobileFacetsWrapper.querySelector('summary');
          if (summary) {
            setTimeout(() => {
              summary.click();
            }, 200);
          }
        }
      }
    } else {
      // Get the form that triggered this (sort form or desktop filter form)
      const form = event.target.closest('form');
      const formsToMerge = form ? [form] : [];
      
      // Also get the other form to preserve it
      // If sort form triggered, also get filter form, and vice versa
      const filterForm = document.getElementById('FacetFiltersForm');
      const mobileFilterForm = document.getElementById('FacetFiltersFormMobile');
      const sortDrawerForm = document.getElementById('FacetSortDrawerForm');
      const sortForm = document.getElementById('FacetSortForm');
      
      // Add the filter form if it exists and isn't already included
      if (filterForm && !formsToMerge.includes(filterForm)) {
        formsToMerge.push(filterForm);
      } else if (mobileFilterForm && !formsToMerge.includes(mobileFilterForm)) {
        formsToMerge.push(mobileFilterForm);
      }
      
      // Add the sort form if it exists and isn't already included
      if (sortDrawerForm && !formsToMerge.includes(sortDrawerForm)) {
        formsToMerge.push(sortDrawerForm);
      }
      if (sortForm && !formsToMerge.includes(sortForm)) {
        formsToMerge.push(sortForm);
      }
      
      const searchParams = this.mergeFormParams(formsToMerge);
      this.onSubmitForm(searchParams, event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
